import { useState, useEffect } from 'react'
import s from './TimeTrackerCalendar.scss'
import { WeekPicker } from 'components/ideal-core-ui'
import moment from 'moment'
import cn from 'classnames'
import { FaPlusCircle, FaClock, FaInfoCircle, FaCaretRight } from 'react-icons/fa'
import { Deal } from './Deal'
import { User } from './User'
import { Summary } from './Summary'
import { utcDate } from 'utils/helpers'
import Select from 'react-select'
import { UncontrolledTooltip } from 'reactstrap'
import { AddButton } from 'components/common/AddButton'
import swal from 'sweetalert'
import { useDispatch } from 'react-redux'
import { makeGetRequest, makeDeleteRequest, makePostRequest } from 'utils/api'
import { toUsers } from '../serializers'
import { LogTimeModal } from './LogTimeModal'
import { isTimetrackerAdmin } from 'utils/authService'

const TimeTrackerCalendar = ({ settings, selectedWeek, setSelectedWeek, handleWeekChange }) => {
  const [usersFilter, setUsersFilter] = useState([])
  const [users, setUsers] = useState([])
  const [showLogTimeModal, setShowLogTimeModal] = useState(false)
  const [logs, setLogs] = useState(null)
  const [editedEntryId, setEditedEntryId] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const dispatch = useDispatch()
  const [summary, setSummary] = useState(null)

  const getLogs = async () => {
    if (!selectedWeek.length) return
    const response = await makeGetRequest(
      `/v1/timetracker/calendar/${utcDate(selectedWeek[0])}?userIds=${usersFilter
        .map(user => user.value)
        .join(',')}`
    )
    setLogs(response)
  }

  const getSummary = async () => {
    const summary = await makePostRequest('/v1/timetracker/calendar/summary', {
      weekStart: utcDate(moment().startOf('week')),
      weekEnd: utcDate(moment().endOf('week')),
      monthStart: utcDate(moment().startOf('month')),
      monthEnd: utcDate(moment().endOf('month')),
      today: utcDate(moment().startOf('day')),
      userIds: usersFilter.map(user => user.value)
    })
    setSummary(summary)
  }

  useEffect(() => {
    ;(async () => {
      if (!isTimetrackerAdmin()) return
      const { content } = await makeGetRequest('/v1/timetracker/users')
      setUsers(toUsers(content))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getLogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersFilter, selectedWeek])

  useEffect(() => {
    getSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersFilter])

  const toggleLogTimeModal = () => {
    reset()
    setShowLogTimeModal(!showLogTimeModal)
  }

  const openEditModal = id => {
    reset()
    setEditedEntryId(id)
    setShowLogTimeModal(true)
  }

  const onDelete = async (id, cb) => {
    const willRemove = await swal({
      title: 'Are you sure you want to delete this log?',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      try {
        await makeDeleteRequest(`/v1/timetracker/${id}`)

        getLogs()
        getSummary()

        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'Log deleted successfuly',
            level: 'success'
          }
        })

        cb && cb()
      } catch (e) {
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'There was a problem deleting log',
            level: 'error'
          }
        })
      }
    }
  }

  const openAddLogOnDateModal = date => {
    reset()
    setShowLogTimeModal(true)
    setSelectedDate(date)
  }

  const reset = () => {
    setEditedEntryId(null)
    setSelectedDate(null)
  }

  const onClone = async id => {
    const willClone = await swal({
      title: 'Are you sure you want to clone this log?',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willClone) {
      try {
        const clonedEntry = await makeGetRequest(`/v1/timetracker/${id}/clone`)
        getLogs()
        getSummary()
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'Log cloned successfuly',
            level: 'success'
          }
        })
        openEditModal(clonedEntry.id)
      } catch (e) {
        dispatch({
          type: 'PUSH_NOTIFICATION',
          payload: {
            title: 'There was a problem cloning log',
            level: 'error'
          }
        })
      }
    }
  }

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <AddButton className={s.logTimeBtn} onClick={toggleLogTimeModal}>
          <FaClock />
          Log Time
        </AddButton>
        {isTimetrackerAdmin() && (
          <>
            <Select
              onChange={values => {
                setUsersFilter(values)
              }}
              options={users}
              className={s.userSelect}
              isMulti
              value={usersFilter}
            />
            <FaInfoCircle id="Type1" style={{ marginTop: 30 }} />
            <UncontrolledTooltip placement="top" target="Type1">
              To see all data, please remove all users from selection.
            </UncontrolledTooltip>
          </>
        )}
        <div className={s.summary}>
          <span>
            Today <FaCaretRight /> {summary?.todayHours ?? 0} hrs &nbsp;&nbsp;&nbsp; |
            &nbsp;&nbsp;&nbsp; This week <FaCaretRight /> {summary?.weekHours ?? 0} hrs
            &nbsp;&nbsp;&nbsp; | &nbsp;&nbsp;&nbsp; This month <FaCaretRight />{' '}
            {summary?.monthHours ?? 0} hrs
          </span>
        </div>
      </div>
      <WeekPicker
        onChange={handleWeekChange}
        weekStartsOn={settings.weekStart === 'MONDAY' ? 1 : 0}
        value={selectedWeek}
        cb={setSelectedWeek}
      />
      {!logs ? (
        <p>No results. Try again with different filters</p>
      ) : (
        <div className={s.calendar}>
          <div className={s.calendarHeader}>
            <Summary summary={logs.header} />
            {selectedWeek.map((day, i) => (
              <div
                key={i}
                className={cn(s.column, s.day, {
                  [s.today]: moment().isSame(day, 'day')
                })}
              >
                <span className={s.bold}>{moment(day).format('DD ddd')}</span>
                <div className={s.actionHeader}>
                  <span className={s.bold}>{logs.header?.dailyHours[i]}hrs</span>
                  <FaPlusCircle
                    className={s.plusIcon}
                    onClick={() => {
                      openAddLogOnDateModal(utcDate(day))
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          {logs.rows?.length > 0 ? (
            logs.rows?.map((row, i) => {
              return (
                <div key={i} className={s.calendarRow}>
                  <div className={cn(s.column, s.user)}>
                    <User user={row.user} totals={row.totals} />
                  </div>
                  {row.days.map(day => {
                    return (
                      <div key={day.workDate} className={s.cell}>
                        {day.deals.map(deal => {
                          return (
                            <Deal
                              key={deal.id}
                              deal={deal}
                              onDelete={() => onDelete(deal.id)}
                              onEdit={() => openEditModal(deal.id)}
                              onClone={() => onClone(deal.id)}
                            />
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )
            })
          ) : (
            <div className={s.noData}>No data. Please check your filter.</div>
          )}
        </div>
      )}
      {showLogTimeModal && (
        <LogTimeModal
          preselectedDate={selectedDate}
          editedEntryId={editedEntryId}
          users={users}
          toggleLogTimeModal={toggleLogTimeModal}
          showLogTimeModal={showLogTimeModal}
          getLogs={getLogs}
          getSummary={getSummary}
          onDelete={toggleLogTimeModal => onDelete(editedEntryId, toggleLogTimeModal)}
        />
      )}
    </>
  )
}

export default TimeTrackerCalendar
